import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ReportFilterOptions } from '@/hooks/useReportFilterOptions';
import { X } from 'lucide-react';
import type { PostingLogFiltersState } from '@/hooks/PostingLog/usePostingLogFilters';

interface PostingLogFilterChipsProps {
  filters: PostingLogFiltersState;
  filterOptions: ReportFilterOptions;
}

export const PostingLogFilterChips: React.FC<PostingLogFilterChipsProps> = ({ filters, filterOptions }) => {
  const {
    appliedFilters,
    setAppliedFilters,
    searchQuery,
    setSearchQuery,
    quickDateRange,
    handleQuickDateChange,
    handleClear,
    activeFilterCount,
  } = filters;
  if (!(activeFilterCount > 0 || searchQuery)) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap mb-4">
      {searchQuery && (
        <Badge variant="secondary" className="gap-1 px-2 py-1 text-xs">
          Search: "{searchQuery}"
          <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-foreground">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      )}
      {quickDateRange !== 'all' && (
        <Badge variant="secondary" className="gap-1 px-2 py-1 text-xs">
          {quickDateRange === 'this-week' ? 'This Week' : quickDateRange === 'last-week' ? 'Last Week' : quickDateRange === 'this-month' ? 'This Month' : 'Last Month'}
          <button onClick={() => handleQuickDateChange('all')} className="ml-1 hover:text-foreground">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      )}
      {appliedFilters.statuses?.map((s) => (
        <Badge key={s} variant="secondary" className="gap-1 px-2 py-1 text-xs">
          {s}
          <button onClick={() => setAppliedFilters((f) => { const next = { ...f }; delete next.statuses; return next; })} className="ml-1 hover:text-foreground">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      {appliedFilters.employeeIds?.map((id) => {
        const emp = filterOptions.employees.find((e) => e.id === id);
        return emp ? (
          <Badge key={id} variant="secondary" className="gap-1 px-2 py-1 text-xs">
            {emp.name}
            <button onClick={() => setAppliedFilters((f) => ({ ...f, employeeIds: f.employeeIds?.filter((e) => e !== id) }))} className="ml-1 hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ) : null;
      })}
      {appliedFilters.failedOnly && (
        <Badge variant="secondary" className="gap-1 px-2 py-1 text-xs">
          Failed Only
          <button onClick={() => setAppliedFilters((f) => { const next = { ...f }; delete next.failedOnly; return next; })} className="ml-1 hover:text-foreground">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      )}
      <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground px-2" onClick={handleClear}>
        Clear all
      </Button>
    </div>
  );
};
